import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import type { ChapterDetail } from '../types';
import { renderMd } from '../md';

export function ChapterPage({ onChange }: { onChange: () => void }) {
  const { id } = useParams();
  const [chapter, setChapter] = useState<ChapterDetail | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    if (!id) return;
    api.getChapter(Number(id)).then((c) => {
      setChapter(c);
      setNotesDraft(c.notes_md ?? '');
    });
  };

  useEffect(() => {
    reload();
  }, [id]);

  if (!chapter) return <div className="muted">Loading…</div>;

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.updateChapter(chapter.id, { notes_md: notesDraft });
      setEditingNotes(false);
      reload();
      onChange();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    form.set('chapter_id', String(chapter.id));
    setBusy(true);
    try {
      await api.uploadReading(form);
      setUploadOpen(false);
      reload();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="muted">Chapter {chapter.number}</div>
      <h2 style={{ marginTop: 4 }}>{chapter.title}</h2>
      {chapter.summary && <p className="muted">{chapter.summary}</p>}

      <div className="card">
        <div className="flex-between">
          <h3 style={{ margin: 0 }}>Reading materials</h3>
          <button
            className="btn secondary"
            onClick={() => setUploadOpen((v) => !v)}
          >
            {uploadOpen ? 'Cancel' : '＋ Upload'}
          </button>
        </div>

        {uploadOpen && (
          <form onSubmit={onUpload} className="col" style={{ marginTop: 12 }}>
            <input name="title" placeholder="Reading title (e.g. SLP Ch.3)" required />
            <textarea name="summary_md" placeholder="Optional summary in markdown" />
            <input type="file" name="file" required />
            <div>
              <button className="btn" disabled={busy}>
                {busy ? 'Uploading…' : 'Upload to R2'}
              </button>
            </div>
          </form>
        )}

        <div style={{ marginTop: 12 }}>
          {chapter.readings.length === 0 && (
            <div className="muted">No readings uploaded yet.</div>
          )}
          {chapter.readings.map((r) => (
            <div key={r.id} className="reading-row">
              <div>
                <div>
                  <a href={api.readingFileUrl(r.id)} target="_blank" rel="noreferrer">
                    {r.title}
                  </a>
                </div>
                <div className="muted">
                  {r.file_name} · {r.size ? `${Math.round(r.size / 1024)} KB` : ''}
                </div>
                {r.summary_md && (
                  <div
                    className="markdown"
                    style={{ marginTop: 8 }}
                    dangerouslySetInnerHTML={{ __html: renderMd(r.summary_md) }}
                  />
                )}
              </div>
              <button
                className="btn danger"
                onClick={async () => {
                  if (!confirm('Delete reading + R2 file?')) return;
                  await api.deleteReading(r.id);
                  reload();
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="flex-between">
          <h3 style={{ margin: 0 }}>My notes</h3>
          {!editingNotes ? (
            <button className="btn secondary" onClick={() => setEditingNotes(true)}>
              Edit
            </button>
          ) : (
            <div className="row">
              <button
                className="btn secondary"
                onClick={() => {
                  setNotesDraft(chapter.notes_md ?? '');
                  setEditingNotes(false);
                }}
              >
                Cancel
              </button>
              <button className="btn" disabled={busy} onClick={saveNotes}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
        {editingNotes ? (
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Write notes in markdown…"
            style={{ minHeight: 220, marginTop: 10 }}
          />
        ) : (
          <div
            className="markdown"
            style={{ marginTop: 10 }}
            dangerouslySetInnerHTML={{
              __html:
                renderMd(chapter.notes_md) ||
                '<p class="muted">No notes yet — click Edit.</p>',
            }}
          />
        )}
      </div>

      {chapter.assignments.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Assignments for this chapter</h3>
          {chapter.assignments.map((a) => (
            <div key={a.id} style={{ padding: '6px 0' }}>
              <span className={`pill ${a.status}`}>{a.status}</span>{' '}
              <strong>{a.title}</strong>{' '}
              {a.due_date && <span className="muted">due {a.due_date}</span>}
            </div>
          ))}
        </div>
      )}

      {chapter.cheatsheet.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Cheatsheet entries</h3>
          {chapter.cheatsheet.map((it) => (
            <div key={it.id} style={{ marginBottom: 8 }}>
              {it.category && <div className="muted">{it.category}</div>}
              <div
                className="markdown"
                dangerouslySetInnerHTML={{ __html: renderMd(it.content_md) }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
